import { create } from "zustand";

import type { TargetChoice } from "../lib/accounts";
import { format, parse } from "../lib/money";
import { normalizeSizes } from "../lib/presets";
import {
  decodeSettings,
  defaultSettings,
  encodeSettings,
  type Notice,
  type Settings,
  type Venue,
} from "../lib/settings";

/**
 * Настройки оператора: хранилище браузера и оркестровка.
 *
 * Правила разбора и миграции живут в lib/settings.ts и проверяются тестом;
 * здесь — только ввод-вывод, тот же раздел «чистое/нечистое», что у
 * book.ts / useBooks.ts и orders.ts / useOrders.ts.
 *
 * ОДИН ключ на весь объект, а не ключ на настройку. Отказ квоты посреди
 * нескольких записей оставил бы набор настроек несогласованным, а
 * несогласованный набор размеров — это ордер неверного размера.
 */

/**
 * Ключ версионирован намеренно.
 *
 * До v2 пресеты хранили РАЗМЕР В SHARE, теперь — СУММУ В ДОЛЛАРАХ. Числа при
 * этом выглядят одинаково: сохранённые «5, 10, 25» разберутся и там, и там, но
 * означают совершенно разные ордера. Прочитать старое значение как новое —
 * худший из возможных исходов, поэтому старый ключ просто не читается: вкладка
 * получает умолчания и тут же засевается списком трейдера (seedFromTrader).
 */
const STORAGE_KEY = "pm_scalper_settings_v2";

interface SettingsStore {
  settings: Settings;
  /**
   * Замечания последней операции: порча данных, отказ хранилища, обрезанный
   * список. Показываются в панели и снимаются оператором.
   */
  notices: Notice[];

  setSizes: (sizes: string[]) => void;
  setActiveSize: (index: number) => void;
  /**
   * Ручная сумма из поля ввода.
   *
   * Пустой текст возвращает режим к пресетам: пустое поле — это не нулевая
   * сумма, а отказ от ручного ввода. Неразбираемый текст СОХРАНЯЕТСЯ (терять
   * набранное посреди набора нельзя), но activeSizeOf на нём даёт null, и
   * панель честно говорит, что размера нет.
   */
  setCustomSize: (raw: string) => void;
  /** Выбрать следующий/предыдущий пресет — для хоткеев и колеса. */
  cycleSize: (delta: number) => void;
  /**
   * Засеять список ПРЕСЕТАМИ трейдера.
   *
   * Только один раз и только если оператор ещё ничего не правил: свежая вкладка
   * обязана быть согласована с трейдером автоматически, но принимать решение за
   * оператора, уже настроившего себе набор, нельзя.
   *
   * Обратное направление — панель задаёт трейдеру, что греть, — живёт в
   * hooks/useWarmSizes.ts. Засев остался ровно для одного случая: браузер, в
   * котором настроек ещё нет.
   */
  seedFromTrader: (sizes: string[]) => void;
  /** Развернуть или свернуть форму ордера на странице рынка. */
  setOrderFormOpen: (open: boolean) => void;
  setPaperMode: (on: boolean) => void;
  /** Выбрать активную строку быстрых действий; null — снять выбор. */
  setTarget: (target: TargetChoice | null) => void;
  /** Выбрать цель формы «Новый ордер»; null — снять выбор. */
  setFormTarget: (target: TargetChoice | null) => void;
  /** Площадка формы ордера: Polymarket или проба predict.fun. */
  setVenue: (venue: Venue) => void;
  dismissNotices: () => void;
}

function load(): { settings: Settings; notices: Notice[] } {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    // Хранилище недоступно целиком (политика «блокировать всё»). Это НЕ повод
    // ронять интерфейс — но и не повод молчать: см. save().
    return { settings: defaultSettings(), notices: [] };
  }
  return decodeSettings(raw);
}

/**
 * Запись с ГРОМКИМ отказом.
 *
 * Здесь сознательное расхождение с api/token.ts, который при отказе хранилища
 * молча уходит в память. Для токена это верно: он доживает до перезагрузки, а
 * его потеря видна как ошибка авторизации. Для настроек — нет: пресет,
 * откатывающийся по F5, оператор заметит уже после отправки ордера неверного
 * размера.
 */
function save(s: Settings): Notice[] {
  try {
    localStorage.setItem(STORAGE_KEY, encodeSettings(s));
    return [];
  } catch {
    return [
      {
        level: "warn",
        message:
          "настройки не сохранены: браузер отказал в хранилище — " +
          "они действуют только до перезагрузки вкладки",
      },
    ];
  }
}

const initial = load();

export const useSettings = create<SettingsStore>((set, get) => ({
  settings: initial.settings,
  notices: initial.notices,

  setSizes: (sizes) => {
    const normalized = normalizeSizes(sizes);
    const next: Settings = {
      ...get().settings,
      sizes: normalized.sizes.length > 0 ? normalized.sizes : get().settings.sizes,
      seeded: true,
    };
    if (next.activeSize >= next.sizes.length) next.activeSize = 0;
    const notices = normalized.notices.map((n): Notice => ({ level: "warn", message: n.message }));
    set({ settings: next, notices: [...notices, ...save(next)] });
  },

  setActiveSize: (index) => {
    const { settings } = get();
    if (index < 0 || index >= settings.sizes.length) return;
    // Выбор пресета — и клик по чипу, и цифра на клавиатуре — возвращает режим
    // к пресетам. Одна и та же строка обслуживает оба пути: отдельной ветки для
    // клавиатуры нет, и разойтись им негде.
    const next: Settings = { ...settings, activeSize: index, sizeMode: "preset" };
    set({ settings: next, notices: save(next) });
  },

  setCustomSize: (raw) => {
    const { settings } = get();
    const next: Settings = {
      ...settings,
      customSize: raw,
      sizeMode: raw.trim() === "" ? "preset" : "custom",
    };
    set({ settings: next, notices: save(next) });
  },

  cycleSize: (delta) => {
    const { settings } = get();
    if (settings.sizes.length === 0) return;
    // Круговой обход в обе стороны: остаток в JS может быть отрицательным.
    const n = settings.sizes.length;
    const index = ((settings.activeSize + delta) % n + n) % n;
    const next = { ...settings, activeSize: index };
    set({ settings: next, notices: save(next) });
  },

  seedFromTrader: (sizes) => {
    const { settings } = get();
    if (settings.seeded) return;
    const normalized = normalizeSizes(sizes);
    if (normalized.sizes.length === 0) return;
    const next: Settings = { ...settings, sizes: normalized.sizes, activeSize: 0, seeded: true };
    set({ settings: next, notices: save(next) });
  },

  setOrderFormOpen: (open) => {
    const next: Settings = { ...get().settings, orderFormOpen: open };
    set({ settings: next, notices: save(next) });
  },

  setPaperMode: (on) => {
    const next: Settings = { ...get().settings, paperMode: on };
    set({ settings: next, notices: save(next) });
  },

  setTarget: (target) => {
    const next: Settings = { ...get().settings, target };
    set({ settings: next, notices: save(next) });
  },

  setFormTarget: (formTarget) => {
    const next: Settings = { ...get().settings, formTarget };
    set({ settings: next, notices: save(next) });
  },

  setVenue: (venue) => {
    const next: Settings = { ...get().settings, venue };
    set({ settings: next, notices: save(next) });
  },

  dismissNotices: () => set({ notices: [] }),
}));

/**
 * Текущий выбранный размер в долларах или null.
 *
 * ЕДИНСТВЕННАЯ точка ответа на вопрос «какая сумма выбрана»: и кнопки, и
 * хоткеи, и подписи читают её отсюда, и потому не могут разойтись в том, чем
 * оператор сейчас торгует.
 *
 * Ручная сумма канонизируется через money.format: «10.0» и «10» обязаны попасть
 * в один билет пред-подписи, иначе кэш промахнётся при исправном кэше.
 */
export function activeSizeOf(s: Settings): string | null {
  if (s.sizeMode === "custom") {
    const value = parse(s.customSize);
    return value === null || value <= 0 ? null : format(value);
  }
  return s.sizes[s.activeSize] ?? s.sizes[0] ?? null;
}

/**
 * Слушатель чужих вкладок.
 *
 * Событие storage приходит ТОЛЬКО из других вкладок — ровно тот случай, ради
 * которого он и нужен: оператор правит пресеты во вкладке A, вкладка B держит
 * старый список, и хоткей в ней отправляет ордер неверного размера.
 *
 * Вне браузера (node-тесты чистых модулей) window отсутствует, поэтому guard.
 */
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    const decoded = decodeSettings(e.newValue);
    useSettings.setState({ settings: decoded.settings, notices: decoded.notices });
  });
}
